import React, { useEffect, useState } from "react";
import {
  getUniversalAccess,
  setUniversalAccess,
  getArmoireConfig,
  setArmoireConfig,
} from "../api/settings";
import { listEntites } from "../api/salles";
import toast from "react-hot-toast";
import { ChevronDown, ChevronUp } from "lucide-react";
import { CONFIG } from "../config/config";

const ROLES_AVAILABLE = ["secretaire", "infirmiere", "medecin", "admin"];
const ROLE_LABELS = {
  secretaire: "Secrétaire",
  infirmiere: "Infirmière",
  medecin: "Médecin",
  admin: "Admin",
};

const INPUT = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300";

export default function AdminSettingsPanel() {
  /* ---------- Accès universel ---------- */
  const [on, setOn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  /* ---------- PAD email par site ---------- */
  const [padOpen, setPadOpen] = useState(false);
  const [padEtabs, setPadEtabs] = useState([]); // [{ trigramme, libelle, email, saving }]
  const [padLoaded, setPadLoaded] = useState(false);

  useEffect(() => {
    if (!padOpen || padLoaded) return;
    (async () => {
      try {
        const entites = await listEntites();
        const etabs = entites.flatMap((ent) => ent.etablissements || []);
        const tk = sessionStorage.getItem('token') || localStorage.getItem('token');
        const rows = await Promise.all(
          etabs.map(async (etb) => {
            try {
              const r = await fetch(
                `${CONFIG.API_URL}/admin/settings/pad-email/${encodeURIComponent(etb.etablissement_trigramme)}`,
                { headers: { Authorization: `Bearer ${tk}` } }
              );
              const d = r.ok ? await r.json() : {};
              return { trigramme: etb.etablissement_trigramme, libelle: etb.etablissement_libelle, email: d.email || '', saving: false };
            } catch {
              return { trigramme: etb.etablissement_trigramme, libelle: etb.etablissement_libelle, email: '', saving: false };
            }
          })
        );
        setPadEtabs(rows);
        setPadLoaded(true);
      } catch {
        toast.error('Impossible de charger les établissements');
      }
    })();
  }, [padOpen, padLoaded]);

  async function savePadEmail(trigramme) {
    const etb = padEtabs.find((e) => e.trigramme === trigramme);
    if (!etb) return;
    setPadEtabs((prev) => prev.map((e) => e.trigramme === trigramme ? { ...e, saving: true } : e));
    try {
      const tk = sessionStorage.getItem('token') || localStorage.getItem('token');
      const r = await fetch(
        `${CONFIG.API_URL}/admin/settings/pad-email/${encodeURIComponent(trigramme)}`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${tk}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: etb.email }),
        }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`Email PAD ${trigramme} enregistré`);
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setPadEtabs((prev) => prev.map((e) => e.trigramme === trigramme ? { ...e, saving: false } : e));
    }
  }

  /* ---------- Armoire ---------- */
  const [armoireOpen, setArmoireOpen] = useState(false);
  const [armoire, setArmoire] = useState({
    enabled: false,
    url: "",
    token: "",
    entity_id: "",
    roles: ["infirmiere", "medecin", "admin"],
  });
  const [armoireSaving, setArmoireSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const isOn = await getUniversalAccess();
        setOn(isOn);
      } catch {
        setErr("Impossible de charger le réglage.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!armoireOpen) return;
    getArmoireConfig()
      .then(setArmoire)
      .catch(() => toast.error("Impossible de charger la config armoire"));
  }, [armoireOpen]);

  async function toggle() {
    try {
      setSaving(true);
      const next = !on;
      await setUniversalAccess(next);
      const check = await getUniversalAccess();
      if (check !== next) {
        setErr("Le serveur n'a pas confirmé la mise à jour.");
        toast.error("Échec de l'enregistrement");
        return;
      }
      setOn(check);
      sessionStorage.setItem("universalAccess", String(check));
      toast.success(check ? "Accès universel activé" : "Accès universel désactivé");
    } catch {
      setErr("Échec de l'enregistrement.");
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  }

  async function saveArmoire(e) {
    e.preventDefault();
    try {
      setArmoireSaving(true);
      await setArmoireConfig(armoire);
      toast.success("Configuration armoire enregistrée");
    } catch (err) {
      toast.error(err.message || "Erreur lors de l'enregistrement");
    } finally {
      setArmoireSaving(false);
    }
  }

  function toggleArmoireRole(role) {
    setArmoire((a) => ({
      ...a,
      roles: a.roles.includes(role)
        ? a.roles.filter((r) => r !== role)
        : [...a.roles, role],
    }));
  }

  return (
    <div className="mt-6 space-y-4">
      {/* ---- Accès universel ---- */}
      <div className="p-4 border rounded-xl bg-white shadow-sm">
        <h2 className="text-lg font-semibold mb-2">Accès universel</h2>
        <p className="text-sm text-gray-600 mb-3">
          Quand c'est activé, tous les utilisateurs authentifiés ont accès à tout
          le parcours. L'onglet <strong>Admin</strong> reste réservé aux admins.
        </p>
        {err && <p className="text-red-600 text-sm mb-2">{err}</p>}
        {loading ? (
          <div className="text-sm text-gray-400">Chargement…</div>
        ) : (
          <button
            onClick={toggle}
            disabled={saving}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${on ? "bg-green-600 text-white hover:bg-green-700" : "bg-gray-100 hover:bg-gray-200"}`}
          >
            {saving ? "Enregistrement…" : on ? "Désactiver" : "Activer"}
          </button>
        )}
      </div>

      {/* ---- Email PAD par site ---- */}
      <div className="border rounded-xl bg-white shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={() => setPadOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition"
        >
          <div>
            <span className="text-base font-semibold">Email PAD par site</span>
            <span className="ml-2 text-xs px-2 py-0.5 rounded font-medium bg-gray-100 text-gray-500">
              Destinataire du bouton PAD dans la fiche patient
            </span>
          </div>
          {padOpen ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
        </button>

        {padOpen && (
          <div className="px-4 pb-4 border-t pt-3 space-y-4">
            <p className="text-sm text-gray-500">
              Définissez l'adresse email qui recevra les demandes de PAD pour chaque site.
              Si aucune adresse n'est configurée, l'email par défaut d'Auteuil est utilisé.
            </p>
            {!padLoaded ? (
              <p className="text-sm text-gray-400">Chargement…</p>
            ) : (
              padEtabs.map((etb) => (
                <div key={etb.trigramme}>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    {etb.libelle} <span className="text-gray-400">({etb.trigramme})</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      className={INPUT}
                      type="email"
                      placeholder="email@site.fr"
                      value={etb.email}
                      onChange={(e) =>
                        setPadEtabs((prev) =>
                          prev.map((x) => x.trigramme === etb.trigramme ? { ...x, email: e.target.value } : x)
                        )
                      }
                    />
                    <button
                      type="button"
                      disabled={etb.saving}
                      onClick={() => savePadEmail(etb.trigramme)}
                      className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60 transition whitespace-nowrap"
                    >
                      {etb.saving ? '…' : 'Enregistrer'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* ---- Armoire médicale ---- */}
      <div className="border rounded-xl bg-white shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={() => setArmoireOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition"
        >
          <div>
            <span className="text-base font-semibold">Armoire médicale sécurisée</span>
            <span className={`ml-2 text-xs px-2 py-0.5 rounded font-medium ${armoire.enabled ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
              {armoire.enabled ? "Activée" : "Désactivée"}
            </span>
          </div>
          {armoireOpen ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
        </button>

        {armoireOpen && (
          <form onSubmit={saveArmoire} className="px-4 pb-4 border-t space-y-4 pt-3">
            <p className="text-sm text-gray-500">
              Connectez un déclencheur Home Assistant pour ouvrir l'armoire médicale depuis la page d'arrivée.
              Le token n'est jamais transmis au navigateur client.
            </p>

            {/* Activation globale */}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={!!armoire.enabled}
                onChange={(e) => setArmoire((a) => ({ ...a, enabled: e.target.checked }))}
              />
              <span className="font-medium">Activer le bouton armoire</span>
            </label>

            {/* URL Home Assistant */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                URL du service Home Assistant
              </label>
              <input
                className={INPUT}
                placeholder="http://172.16.1.90:8123/api/services/input_button/press"
                value={armoire.url}
                onChange={(e) => setArmoire((a) => ({ ...a, url: e.target.value }))}
              />
            </div>

            {/* Token HA */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                Token d'autorisation Home Assistant
              </label>
              <input
                className={INPUT}
                type="password"
                placeholder="eyJhbGci…"
                value={armoire.token}
                onChange={(e) => setArmoire((a) => ({ ...a, token: e.target.value }))}
              />
              <p className="text-xs text-gray-400 mt-1">Stocké côté serveur uniquement, jamais transmis au navigateur.</p>
            </div>

            {/* Entity ID */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                Entity ID (ex: input_button.armoire_medicale)
              </label>
              <input
                className={INPUT}
                placeholder="input_button.test_bouton_armoire"
                value={armoire.entity_id}
                onChange={(e) => setArmoire((a) => ({ ...a, entity_id: e.target.value }))}
              />
            </div>

            {/* Rôles autorisés */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">
                Rôles autorisés à voir et utiliser le bouton
              </label>
              <div className="flex flex-wrap gap-2">
                {ROLES_AVAILABLE.map((role) => (
                  <label
                    key={role}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition
                      ${armoire.roles.includes(role)
                        ? "bg-red-50 border-red-300 text-red-700 font-medium"
                        : "bg-gray-50 border-gray-200 text-gray-500"
                      }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={armoire.roles.includes(role)}
                      onChange={() => toggleArmoireRole(role)}
                    />
                    {ROLE_LABELS[role]}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={armoireSaving}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60 transition"
              >
                {armoireSaving ? "Enregistrement…" : "Enregistrer la configuration"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
